import { ArrowLeft, Trash01 } from '@untitledui/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Tab, TabList, Tabs } from '@/lib/base/tabs';
import { builderConversationPath } from '@/lib/crm/hooks/useBuilderMode';
import { useEnsuredStoreObjects } from '@/lib/crm/hooks/useEnsuredStoreObjects';
import { ApiError, isTransientError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';
import { getBuilderRowLabel } from '@/lib/reasoning/builderRowLabel';
import type { ConversationLifecycleState } from '@/lib/reasoning/conversationLifecycleState';
import { useBuilderConversations } from '@/lib/reasoning/hooks/useBuilderConversations';
import type { CrmObject } from '@/types/crm';

import { useTitle } from '../../hooks/useTitle';

/**
 * Which slice of history is showing. Builder conversations are excluded from
 * the ordinary list server-side, so without this they are unreachable from
 * here — the page the "See all" link lands on.
 */
type HistoryMode = 'chats' | 'builds';

interface ConversationListItem {
  id: string;
  title: string;
  initial_prompt: string;
  state: ConversationLifecycleState;
  created_at: string;
  updated_at: string;
  total_chains: number;
}

interface Artifact {
  id: number;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Artifact content varies by type (email has subject/body, others different)
  content: any;
  status: 'draft' | 'final' | 'sent' | 'published';
  version_number: number;
  version_notes: string;
  is_current_version: boolean;
  parent_artifact: number | null;
  version_count: number;
  has_versions: boolean;
  artifact_type: {
    name: string;
    description: string;
  };
  created_at: string;
  updated_at: string;
  conversation_id?: number;
  conversation_title?: string;
  email_details?: {
    to_addresses: string[];
    cc_addresses: string[];
    bcc_addresses: string[];
    sent_at: string | null;
    delivery_status: string;
  };
}

/**
 * Extract the visible text of an HTML email body for a plain-text preview.
 *
 * Parses via DOMParser into an inert document and reads `textContent` rather
 * than regex-stripping tags. A regex like `/<[^>]*>/g` is incomplete multi-
 * character sanitization (CodeQL) — it mishandles malformed/unterminated tags
 * and entity-encoded angle brackets — whereas DOMParser yields the true text
 * without executing scripts or fetching resources. Mirrors the DOMParser
 * approach in AskUserForInfoRenderer.tsx.
 */
function htmlToPlainText(html: string): string {
  return (
    new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
  );
}

export default function ConversationsManager() {
  useTitle('Previous chats');

  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();

  // Tab state
  const [activeTab, _setActiveTab] = useState<'conversations' | 'artifacts'>(
    'conversations'
  );

  // Chats/Builds slice, independent of the conversations/artifacts tab above.
  const [mode, setMode] = useState<HistoryMode>('chats');

  // Conversations state management
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    []
  );
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState<unknown>(null);
  const [conversationsInitialLoaded, setConversationsInitialLoaded] =
    useState(false);

  // Artifacts state management
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactsError, setArtifactsError] = useState<unknown>(null);
  const [artifactsInitialLoaded, setArtifactsInitialLoaded] = useState(false);

  // Shared filters
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const conversationsAbortControllerRef = useRef<AbortController | null>(null);
  const artifactsAbortControllerRef = useRef<AbortController | null>(null);
  const previousSearchQuery = useRef<string>('');

  const itemsPerPage = 12;

  // Helper function for human-readable time formatting
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    }
    if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m ago`;
    }
    if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    }
    if (diffInSeconds < 604800) {
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const loadConversations = useCallback(async () => {
    // Cancel previous request if still in flight
    if (conversationsAbortControllerRef.current) {
      conversationsAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    conversationsAbortControllerRef.current = controller;

    if (!orgId) {
      return;
    }

    try {
      setConversationsLoading(true);
      setConversationsError(null);

      // Add search query parameter if present
      let url = `/api/orgs/${orgId}/reasoning/conversations/`;
      if (searchQuery.trim()) {
        url += `?search=${encodeURIComponent(searchQuery.trim())}`;
      }

      const response = await fetchWithDefaults(url, {
        signal: controller.signal,
      });

      if (!response.ok) {
        // Throw a status-aware ApiError so isTransientError can recognize
        // 5xx server failures as transient (a plain Error drops the status).
        throw new ApiError('Failed to load conversations', response.status);
      }

      const data = await response.json();
      setConversations(data.conversations || []);
      setConversationsInitialLoaded(true);
      setConversationsLoading(false);
      setIsSearching(false);
    } catch (error: unknown) {
      // Don't update loading state for aborted requests - prevents flash
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Failed to load conversations:', error);
      setConversationsError(error);
      setConversationsLoading(false);
      setIsSearching(false);
    }
  }, [orgId, searchQuery]);

  const loadArtifacts = useCallback(async () => {
    // Cancel previous request if still in flight
    if (artifactsAbortControllerRef.current) {
      artifactsAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    artifactsAbortControllerRef.current = controller;

    if (!orgId) {
      return;
    }

    try {
      setArtifactsLoading(true);
      setArtifactsError(null);

      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/artifacts/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new ApiError('Failed to load artifacts', response.status);
      }

      const data = await response.json();
      setArtifacts(data.artifacts || []);
      setArtifactsInitialLoaded(true);
    } catch (error: unknown) {
      // Ignore aborted requests
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Failed to load artifacts:', error);
      setArtifactsError(error);
    } finally {
      setArtifactsLoading(false);
      setIsSearching(false);
    }
  }, [orgId]);

  // Load conversations on mount (artifacts lazy loaded on tab switch)
  useEffect(() => {
    loadConversations();

    // Cleanup: abort ongoing conversations request when component unmounts
    return () => {
      if (conversationsAbortControllerRef.current) {
        conversationsAbortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Lazy load artifacts when switching to Materials tab
  useEffect(() => {
    if (
      activeTab === 'artifacts' &&
      !artifactsInitialLoaded &&
      !artifactsLoading
    ) {
      loadArtifacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, artifactsInitialLoaded, artifactsLoading]);

  // Debounced search effect - only triggers on actual search query changes
  useEffect(() => {
    // Skip if searchQuery hasn't actually changed (handles StrictMode remounts)
    if (searchQuery === previousSearchQuery.current) {
      return;
    }

    // Update the previous value
    previousSearchQuery.current = searchQuery;

    setIsSearching(true);
    const timer = setTimeout(() => {
      if (activeTab === 'conversations') {
        loadConversations();
      } else {
        loadArtifacts();
      }
    }, 300); // 300ms debounce

    // Cleanup: clear timer and abort any ongoing requests
    return () => {
      clearTimeout(timer);
      if (
        activeTab === 'conversations' &&
        conversationsAbortControllerRef.current
      ) {
        conversationsAbortControllerRef.current.abort();
      } else if (
        activeTab === 'artifacts' &&
        artifactsAbortControllerRef.current
      ) {
        artifactsAbortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]); // Only re-run when searchQuery changes, not when tab changes

  // Conversations are already filtered and sorted by backend
  const filteredAndSortedConversations = conversations;

  // Builder conversations. Fetched unconditionally so the toggle's visibility
  // is decided from the resolved list rather than a loading flag — otherwise it
  // flashes in after the page has already painted.
  const {
    data: builderData,
    isLoading: builderLoading,
    error: builderError,
  } = useBuilderConversations(orgId);
  const builderConversations = useMemo(
    () => builderData?.conversations ?? [],
    [builderData]
  );
  const hasBuilderConversations = builderConversations.length > 0;
  // An org that has never used builder mode never sees the toggle, and so can
  // never be stranded in a mode it cannot leave.
  const effectiveMode: HistoryMode = hasBuilderConversations ? mode : 'chats';

  const builderEntityIds = useMemo(
    () =>
      builderConversations
        .map(c => c.builder_entity_id)
        .filter((id): id is string => !!id),
    [builderConversations]
  );
  const orgIdNumber = orgId ? Number(orgId) : undefined;
  // Batch-resolve display names through the store rather than one fetch per
  // row; only ensure (fetch) while the builds slice is actually showing.
  const { objects: resolvedBuilderObjects } = useEnsuredStoreObjects(
    orgIdNumber,
    builderEntityIds,
    { ensure: effectiveMode === 'builds' }
  );
  const resolvedByEntityId = useMemo(() => {
    const map = new Map<string, CrmObject>();
    for (const resolved of resolvedBuilderObjects) {
      if (resolved.object) {
        map.set(resolved.entityId, resolved.object);
      }
    }
    return map;
  }, [resolvedBuilderObjects]);

  // Label first, then filter: the search box matches what the row actually
  // says (the entity being built), not the conversation's generated title.
  // Filtering is client-side because the builder endpoint takes no `search`
  // param — the whole slice is already in memory.
  const filteredBuilderConversations = useMemo(() => {
    const labelled = builderConversations.map(conv => ({
      conv,
      label: getBuilderRowLabel(conv, resolvedByEntityId),
    }));
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return labelled;
    }
    return labelled.filter(row => row.label.toLowerCase().includes(query));
  }, [builderConversations, resolvedByEntityId, searchQuery]);

  // Filter and sort artifacts (frontend filtering for now)
  const filteredAndSortedArtifacts = React.useMemo(() => {
    let filtered = artifacts;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        artifact =>
          (artifact.title || '').toLowerCase().includes(query) ||
          (artifact.conversation_title || '').toLowerCase().includes(query) ||
          (artifact.artifact_type?.name || '').toLowerCase().includes(query)
      );
    }

    // Sort by recent
    filtered.sort((a, b) => {
      return (
        new Date(b.updated_at || b.created_at || 0).getTime() -
        new Date(a.updated_at || a.created_at || 0).getTime()
      );
    });

    return filtered;
  }, [artifacts, searchQuery]);

  // Pagination for conversations
  const conversationsTotalPages = Math.ceil(
    filteredAndSortedConversations.length / itemsPerPage
  );
  const paginatedConversations = filteredAndSortedConversations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Pagination for artifacts
  const artifactsTotalPages = Math.ceil(
    filteredAndSortedArtifacts.length / itemsPerPage
  );
  const paginatedArtifacts = filteredAndSortedArtifacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Pagination for builds — same page size, so switching modes keeps the
  // page furniture consistent.
  const buildsTotalPages = Math.ceil(
    filteredBuilderConversations.length / itemsPerPage
  );
  const paginatedBuilderConversations = filteredBuilderConversations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const showingBuilds =
    activeTab === 'conversations' && effectiveMode === 'builds';

  // Current data based on active tab, then the chats/builds slice within it.
  let currentItems: unknown[];
  let currentLoading: boolean;
  let currentError: unknown;
  let errorLabel: string;
  let totalPages: number;
  if (activeTab === 'artifacts') {
    currentItems = filteredAndSortedArtifacts;
    currentLoading = artifactsLoading;
    currentError = artifactsError;
    errorLabel = "We couldn't load artifacts";
    totalPages = artifactsTotalPages;
  } else if (showingBuilds) {
    currentItems = filteredBuilderConversations;
    currentLoading = builderLoading;
    currentError = builderError;
    errorLabel = "We couldn't load builds";
    totalPages = buildsTotalPages;
  } else {
    currentItems = filteredAndSortedConversations;
    currentLoading = conversationsLoading;
    currentError = conversationsError;
    errorLabel = "We couldn't load conversations";
    totalPages = conversationsTotalPages;
  }

  const handleModeChange = useCallback((key: string | number) => {
    setMode(key === 'builds' ? 'builds' : 'chats');
    // Page numbers do not carry across slices — page 3 of chats is rarely a
    // valid page of builds, and landing on an empty page reads as a bug.
    setCurrentPage(1);
  }, []);

  const goToBuilderConversation = useCallback(
    (conversationId: string) => {
      if (!orgId) {
        console.error('orgId is undefined, cannot navigate');
        return;
      }
      navigate(builderConversationPath(orgId, conversationId));
    },
    [navigate, orgId]
  );

  const goToConversation = (conversationId: string) => {
    if (!orgId) {
      console.error('orgId is undefined, cannot navigate');
      return;
    }
    navigate(`/${orgId}/conversations/${conversationId}`);
  };

  const goToArtifact = (artifact: Artifact) => {
    if (!orgId) {
      console.error('orgId is undefined, cannot navigate');
      return;
    }
    if (artifact.conversation_id) {
      navigate(
        `/${orgId}/conversations/${artifact.conversation_id}#artifact-${artifact.id}`
      );
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (!orgId) {
      return;
    }

    try {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete conversation');
      }

      // Reload conversations
      loadConversations();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error("We couldn't delete this conversation. Try again.");
    }
  };

  // Helper functions for artifacts display
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'draft':
        return {
          color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
          label: 'Draft',
        };
      case 'final':
        return {
          color: 'bg-green-50 text-green-700 border-green-200',
          label: 'Final',
        };
      case 'sent':
        return {
          color: 'bg-blue-50 text-blue-700 border-blue-200',
          label: 'Sent',
        };
      case 'published':
        return {
          color: 'bg-blue-50 text-blue-700 border-blue-200',
          label: 'Published',
        };
      default:
        return {
          color: 'bg-gray-50 text-gray-700 border-gray-200',
          label: 'Processing',
        };
    }
  };

  const getTypeDisplay = (typeName: string) => {
    switch (typeName) {
      case 'email':
        return {
          icon: (
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
          ),
          gradient: 'from-blue-500 to-blue-600',
          label: 'Email',
        };
      case 'document':
        return {
          icon: (
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          ),
          gradient: 'from-emerald-500 to-emerald-600',
          label: 'Document',
        };
      case 'code':
        return {
          icon: (
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4'
              />
            </svg>
          ),
          gradient: 'from-purple-500 to-purple-600',
          label: 'Code',
        };
      default:
        return {
          icon: (
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          ),
          gradient: 'from-neutral-500 to-neutral-600',
          label: 'Artifact',
        };
    }
  };

  const handleBackClick = () => {
    if (!orgId) {
      console.error('orgId is undefined, cannot navigate');
      return;
    }
    navigate(`/${orgId}/conversations`);
  };

  return (
    <div className='min-h-full bg-primary'>
      <div className='max-w-4xl mx-auto px-4 py-8 space-y-6'>
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className='flex items-center gap-2 text-sm font-medium text-tertiary hover:text-primary transition-colors cursor-pointer'
        >
          <ArrowLeft className='size-4' strokeWidth={2} />
          <span>Back to home</span>
        </button>

        {/* Header */}
        <div className='space-y-2'>
          <h1 className='text-[24px] font-medium text-black dark:text-white'>
            Previous chats
          </h1>
          <p className='text-[14px] text-neutral-600 dark:text-neutral-400'>
            Manage and search through your conversations
          </p>
        </div>

        {/* Chats/Builds toggle — only offered when there is something to
            toggle to (see hasBuilderConversations above). */}
        {activeTab === 'conversations' && hasBuilderConversations && (
          <Tabs
            selectedKey={effectiveMode}
            onSelectionChange={handleModeChange}
          >
            <TabList aria-label='Conversation history filter' className='gap-4'>
              <Tab id='chats'>Chats</Tab>
              <Tab id='builds'>Builds</Tab>
            </TabList>
          </Tabs>
        )}

        {/* Search */}
        <div className='relative'>
          <svg
            className='absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-fg-quaternary'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            strokeWidth={2}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            />
          </svg>
          <input
            type='text'
            placeholder={
              showingBuilds ? 'Search builds' : 'Search conversations'
            }
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className='w-full pl-10 pr-10 py-2.5 bg-primary rounded-lg text-md font-medium text-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset placeholder:text-fg-quaternary focus:ring-2 focus:ring-brand'
          />
          {isSearching && (
            <div className='absolute right-3.5 top-1/2 -translate-y-1/2'>
              <LoadingIndicator inline size='xs' />
            </div>
          )}
        </div>

        {/* Loading State - Skeleton (only show on initial load) */}
        {currentLoading &&
          activeTab === 'conversations' &&
          (showingBuilds || !conversationsInitialLoaded) && (
            <div className='border-t border-secondary'>
              {[0, 1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  className='flex items-center justify-between py-4 -mx-4 px-4 border-b border-secondary last:border-b-0'
                >
                  {/* Title - matches: text-sm font-medium truncate flex-1 */}
                  <div className='h-4 bg-base-300 rounded animate-pulse flex-1 max-w-md' />
                  <div className='flex items-center gap-4 flex-shrink-0'>
                    {/* Chains count - matches: text-xs text-tertiary */}
                    <div className='h-3 w-14 bg-base-300 rounded animate-pulse' />
                    {/* Timestamp - matches: text-xs text-tertiary */}
                    <div className='h-3 w-12 bg-base-300 rounded animate-pulse' />
                    {/* Delete button - matches: size-4 icon in p-1 button */}
                    <div className='size-4 bg-base-300 rounded animate-pulse' />
                  </div>
                </div>
              ))}
            </div>
          )}
        {currentLoading &&
          activeTab === 'artifacts' &&
          !artifactsInitialLoaded && (
            <div className='space-y-2'>
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className='bg-primary rounded-lg border border-secondary p-4'
                >
                  <div className='flex items-start gap-4'>
                    <div className='w-8 h-8 bg-base-300 rounded-lg animate-pulse flex-shrink-0' />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-start justify-between gap-4 mb-3'>
                        <div className='h-6 bg-base-300 rounded animate-pulse flex-1 max-w-xs' />
                        <div className='flex items-center gap-3 flex-shrink-0'>
                          <div className='h-5 w-14 bg-base-300 rounded-md animate-pulse' />
                          <div className='h-3 w-6 bg-base-300 rounded animate-pulse' />
                        </div>
                      </div>
                      <div className='space-y-2 mb-4'>
                        <div className='h-4 bg-base-300 rounded animate-pulse w-full' />
                        <div className='h-4 bg-base-300 rounded animate-pulse w-3/4' />
                      </div>
                      <div className='flex items-center gap-4'>
                        <div className='h-3 w-16 bg-base-300 rounded animate-pulse' />
                        <div className='h-3 w-24 bg-base-300 rounded animate-pulse' />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* Error State */}
        {!!currentError && (
          <div className='bg-[var(--color-bg-error-secondary)] border border-[var(--color-border-error)] rounded-lg p-4'>
            <div className='flex items-center gap-3 text-[var(--color-text-error-primary)]'>
              <svg
                className='w-5 h-5 flex-shrink-0'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <span className='text-sm'>
                {isTransientError(currentError)
                  ? 'Trouble connecting.'
                  : errorLabel}
              </span>
            </div>
          </div>
        )}

        {/* No Results */}
        {!currentLoading &&
          !currentError &&
          currentItems.length === 0 &&
          (showingBuilds ||
            (activeTab === 'conversations'
              ? conversationsInitialLoaded
              : artifactsInitialLoaded)) && (
            <div className='text-center py-12'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <svg
                  className='w-8 h-8 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z'
                  />
                </svg>
              </div>
              <h3 className='text-lg font-semibold text-primary mb-2'>
                {(() => {
                  const noun = showingBuilds ? 'builds' : activeTab;
                  return searchQuery ? `No ${noun} found` : `No ${noun} yet`;
                })()}
              </h3>
              <p className='text-sm text-tertiary'>
                {(() => {
                  if (searchQuery) {
                    return "Try adjusting your search to find what you're looking for.";
                  } else if (showingBuilds) {
                    return 'Automations you build in chat will appear here.';
                  } else if (activeTab === 'conversations') {
                    return 'Start your first conversation to see it here.';
                  } else {
                    return 'Materials will appear here when created during conversations.';
                  }
                })()}
              </p>
            </div>
          )}

        {/* Conversations List */}
        {activeTab === 'conversations' &&
          !showingBuilds &&
          !currentLoading &&
          !currentError &&
          paginatedConversations.length > 0 && (
            <div className='border-t border-secondary'>
              {paginatedConversations.map(conv => (
                <div
                  key={conv.id}
                  className='flex items-center justify-between py-4 cursor-pointer hover:bg-bg-secondary/50 transition-colors -mx-4 px-4 border-b border-secondary last:border-b-0'
                  onClick={() => goToConversation(conv.id)}
                >
                  <p
                    className='text-sm font-medium text-primary truncate flex-1'
                    title={conv.title || 'Untitled conversation'}
                  >
                    {conv.title || 'Untitled conversation'}
                  </p>
                  <div className='flex items-center gap-4 flex-shrink-0'>
                    {conv.total_chains > 0 && (
                      <span className='text-xs text-tertiary'>
                        {conv.total_chains} chain
                        {conv.total_chains !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className='text-xs text-tertiary'>
                      {formatTimeAgo(conv.updated_at || conv.created_at || '')}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteConversation(conv.id);
                      }}
                      className='p-1 hover:bg-error/10 rounded transition-colors cursor-pointer'
                      title='Delete conversation'
                      aria-label={`Delete ${conv.title || 'conversation'}`}
                    >
                      <Trash01
                        className='size-4 text-tertiary hover:text-error transition-colors'
                        strokeWidth={2}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* Builds List — same row geometry as chats, but labelled by what is
            being built and linking into builder mode rather than the ordinary
            chat route. No delete affordance: deleting a builder conversation
            would not remove the automation it created, so offering it here
            would imply a destructive effect it does not have. */}
        {showingBuilds &&
          !currentLoading &&
          !currentError &&
          paginatedBuilderConversations.length > 0 && (
            <div className='border-t border-secondary'>
              {paginatedBuilderConversations.map(({ conv, label }) => (
                <div
                  key={conv.id}
                  className='flex items-center justify-between py-4 cursor-pointer hover:bg-bg-secondary/50 transition-colors -mx-4 px-4 border-b border-secondary last:border-b-0'
                  onClick={() => goToBuilderConversation(String(conv.id))}
                >
                  <p
                    className='text-sm font-medium text-primary truncate flex-1'
                    title={label}
                  >
                    {label}
                  </p>
                  <div className='flex items-center gap-4 flex-shrink-0'>
                    <span className='text-xs text-tertiary'>
                      {formatTimeAgo(conv.created_at || '')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* Artifacts List - Content-First Design */}
        {activeTab === 'artifacts' &&
          !currentLoading &&
          !currentError &&
          paginatedArtifacts.length > 0 && (
            <div className='space-y-2'>
              {paginatedArtifacts.map(artifact => {
                const typeDisplay = getTypeDisplay(artifact.artifact_type.name);
                const statusDisplay = getStatusDisplay(artifact.status);

                // Extract meaningful content preview
                const getContentPreview = (artifact: Artifact) => {
                  if (artifact.artifact_type.name === 'email') {
                    // For emails, show subject and body preview
                    const subject = artifact.content?.subject || '';
                    const body =
                      artifact.content?.body ||
                      artifact.content?.body_html ||
                      '';
                    const preview = htmlToPlainText(body).substring(0, 200);
                    return { subject, preview };
                  }
                  // For other types, show content preview
                  const contentStr =
                    typeof artifact.content === 'string'
                      ? artifact.content
                      : JSON.stringify(artifact.content, null, 2);
                  return { preview: contentStr.substring(0, 200) };
                };

                const content = getContentPreview(artifact);

                return (
                  <div
                    key={artifact.id}
                    onClick={() => goToArtifact(artifact)}
                    className='cursor-pointer transition-colors hover:bg-bg-secondary/50 bg-primary rounded-lg border border-secondary mb-2'
                  >
                    <div className='px-4 py-4'>
                      <div className='flex items-start gap-4'>
                        {/* Minimal Type Indicator */}
                        <div
                          className={`w-8 h-8 rounded-lg bg-gradient-to-br ${typeDisplay.gradient} flex items-center justify-center text-white flex-shrink-0 mt-1`}
                        >
                          <div className='w-4 h-4'>{typeDisplay.icon}</div>
                        </div>

                        {/* Main Content Area */}
                        <div className='flex-1 min-w-0'>
                          {/* Header Row */}
                          <div className='flex items-start justify-between gap-4 mb-3'>
                            <div className='flex-1 min-w-0'>
                              <h3 className='text-lg font-semibold text-primary group-hover:text-utility-brand-600 transition-colors leading-tight'>
                                {artifact.title}
                              </h3>
                              {artifact.artifact_type.name === 'email' &&
                                content.subject &&
                                content.subject !== artifact.title && (
                                  <p className='text-sm text-secondary mt-1 leading-relaxed'>
                                    Subject: {content.subject}
                                  </p>
                                )}
                            </div>

                            {/* Status & Actions Row */}
                            <div className='flex items-center gap-3 flex-shrink-0'>
                              <div
                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusDisplay.color}`}
                              >
                                {statusDisplay.label}
                              </div>
                              <span className='text-xs text-tertiary'>
                                v{artifact.version_number}
                              </span>
                            </div>
                          </div>

                          {/* Content Preview - The Star of the Show */}
                          <div className='mb-4'>
                            <p className='text-sm text-primary/80 leading-relaxed line-clamp-3'>
                              {content.preview}
                              {content.preview.length >= 200 && '…'}
                            </p>
                          </div>

                          {/* Email Recipients (if applicable) */}
                          {artifact.email_details && (
                            <div className='mb-3 p-3 bg-utility-brand-50 border border-utility-brand-200 rounded-lg'>
                              <p className='text-xs text-utility-brand-700'>
                                <span className='font-medium'>To:</span>{' '}
                                {artifact.email_details.to_addresses.join(', ')}
                              </p>
                              {artifact.email_details.cc_addresses?.length >
                                0 && (
                                <p className='text-xs text-utility-brand-700 mt-1'>
                                  <span className='font-medium'>CC:</span>{' '}
                                  {artifact.email_details.cc_addresses.join(
                                    ', '
                                  )}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Footer Metadata */}
                          <div className='flex items-center justify-between text-xs text-quaternary'>
                            <div className='flex items-center gap-4'>
                              <span className='inline-flex items-center gap-1.5'>
                                <div className='w-3 h-3 rounded bg-bg-secondary' />
                                {typeDisplay.label}
                              </span>
                              {artifact.conversation_title && (
                                <span className='inline-flex items-center gap-1.5 text-tertiary'>
                                  <svg
                                    className='w-3 h-3'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
                                    />
                                  </svg>
                                  {artifact.conversation_title}
                                </span>
                              )}
                            </div>
                            <span>
                              {formatTimeAgo(
                                artifact.updated_at || artifact.created_at
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-center gap-2 pt-4'>
            <Button
              color='secondary'
              size='sm'
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>

            <div className='flex items-center gap-1'>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                if (pageNum < 1 || pageNum > totalPages) {
                  return null;
                }

                return (
                  <Button
                    key={pageNum}
                    color={currentPage === pageNum ? 'primary' : 'secondary'}
                    size='sm'
                    onClick={() => setCurrentPage(pageNum)}
                    className='!w-10 !px-0'
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              color='secondary'
              size='sm'
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
